/**
 * EventCenter - Standalone Event Emitter
 * 
 * OPTIMIZATION: This no longer depends on Phaser, allowing Phaser to be lazy loaded.
 * Uses a simple EventEmitter implementation compatible with Phaser's API.
 */

type EventCallback = (...args: any[]) => void

class SimpleEventEmitter {
  private events: Map<string, Set<EventCallback>> = new Map()
  private onceEvents: Map<string, Set<EventCallback>> = new Map()
  // Maps original callback -> bound callback per event, so off() can find the
  // bound version when context was used during on()/once().
  private boundMap: Map<string, Map<EventCallback, EventCallback>> = new Map()

  on(event: string, callback: EventCallback, context?: any): this {
    const boundCallback = context ? callback.bind(context) : callback
    if (!this.events.has(event)) {
      this.events.set(event, new Set())
    }
    this.events.get(event)!.add(boundCallback)
    // Track original -> bound mapping so off(event, originalFn) works
    if (context) {
      if (!this.boundMap.has(event)) {
        this.boundMap.set(event, new Map())
      }
      this.boundMap.get(event)!.set(callback, boundCallback)
    }
    return this
  }

  once(event: string, callback: EventCallback, context?: any): this {
    const boundCallback = context ? callback.bind(context) : callback
    if (!this.onceEvents.has(event)) {
      this.onceEvents.set(event, new Set())
    }
    this.onceEvents.get(event)!.add(boundCallback)
    if (context) {
      if (!this.boundMap.has(event)) {
        this.boundMap.set(event, new Map())
      }
      this.boundMap.get(event)!.set(callback, boundCallback)
    }
    return this
  }

  off(event: string, callback?: EventCallback): this {
    if (callback) {
      // Resolve the actual stored function (may be a bound version)
      const resolved = this.boundMap.get(event)?.get(callback) ?? callback
      this.events.get(event)?.delete(resolved)
      this.onceEvents.get(event)?.delete(resolved)
      this.boundMap.get(event)?.delete(callback)
    } else {
      this.events.delete(event)
      this.onceEvents.delete(event)
      this.boundMap.delete(event)
    }
    return this
  }

  emit(event: string, ...args: any[]): boolean {
    let called = false

    // Regular listeners
    const listeners = this.events.get(event)
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(...args)
          called = true
        } catch (error) {
          console.error(`[EventCenter] Error in event handler for "${event}":`, error)
        }
      })
    }

    // Once listeners (remove after calling)
    const onceListeners = this.onceEvents.get(event)
    if (onceListeners) {
      onceListeners.forEach(callback => {
        try {
          callback(...args)
          called = true
        } catch (error) {
          console.error(`[EventCenter] Error in once handler for "${event}":`, error)
        }
      })
      this.onceEvents.delete(event)
    }

    return called
  }

  removeListener(event: string, callback: EventCallback): this {
    return this.off(event, callback)
  }

  removeAllListeners(event?: string): this {
    if (event) {
      this.events.delete(event)
      this.onceEvents.delete(event)
      this.boundMap.delete(event)
    } else {
      this.events.clear()
      this.onceEvents.clear()
      this.boundMap.clear()
    }
    return this
  }

  listenerCount(event: string): number {
    return (this.events.get(event)?.size || 0) + (this.onceEvents.get(event)?.size || 0)
  }
}

export const phaserEvents = new SimpleEventEmitter()

export enum Event {
  PLAYER_JOINED = 'player-joined',
  PLAYER_UPDATED = 'player-updated',
  PLAYER_LEFT = 'player-left',
  PLAYER_DISCONNECTED = 'player-disconnected',
  PLAYER_CORRECTION = 'player-correction',
  MY_PLAYER_READY = 'my-player-ready',
  MY_PLAYER_NAME_CHANGE = 'my-player-name-change',
  MY_PLAYER_TEXTURE_CHANGE = 'my-player-texture-change',
  MY_PLAYER_ACTIVITY_CHANGE = 'my-player-activity-change',
  MY_PLAYER_VIDEO_CONNECTED = 'my-player-video-connected',
  ITEM_USER_ADDED = 'item-user-added',
  ITEM_USER_REMOVED = 'item-user-removed',
  UPDATE_DIALOG_BUBBLE = 'update-dialog-bubble',
  NETWORK_CONNECTED = 'network-connected',
  NETWORK_DISCONNECTED = 'network-disconnected',
  CHAT_MESSAGE_RECEIVED = 'chat-message-received',
  WHITEBOARD_OPEN = 'whiteboard-open',
  WHITEBOARD_CLOSE = 'whiteboard-close',
  PLAYER_RETURN_TO_LOBBY = 'playerReturnToLobby',
  SHOW_PLAYER_INFO = 'show-player-info',
  HIDE_PLAYER_INFO = 'hide-player-info',
  UPDATE_PLAYER_INFO = 'update-player-info',
  UPDATE_CURRENT_PLAYER_INFO = 'update-current-player-info',
  ROOM_STATE_CHANGED = 'room-state-changed',
  ROOM_LEFT = 'room-left',
  ROOM_PLAYER_COUNT = 'room-player-count',
  WEBRTC_PEER_CONNECTED = 'webrtc-peer-connected',
  WEBRTC_PEER_DISCONNECTED = 'webrtc-peer-disconnected',
  WEBRTC_PEER_ERROR = 'webrtc-peer-error',
  WEBRTC_PEER_RECONNECTED = 'webrtc-peer-reconnected',
  PLAYER_READY = 'player-ready',
  PLAYER_MOVED = 'player-moved',
  PLAYER_ANIMATION_CHANGE = 'player-animation-change',
  PLAYER_DIRECTION_CHANGE = 'player-direction-change',
  PLAYER_POSITION_UPDATE = 'player-position-update',
  LIVEKIT_TOKEN_RECEIVED = 'livekit-token-received',
  LIVEKIT_ERROR_RECEIVED = 'livekit-error-received',
  LIVEKIT_RECONNECT_REQUESTED = 'livekit-reconnect-requested',
  /** Colyseus token-based room reconnect succeeded — LiveKit should refresh JWT and reconnect if needed */
  COLYSEUS_RECONNECTED = 'colyseus-reconnected',
  // Virtual Room Area Events
  VIRTUAL_ROOM_AREAS_TOGGLED = 'virtual-room-areas-toggled',
  VIRTUAL_ROOM_AREAS_LOADED = 'virtual-room-areas-loaded',
  VIRTUAL_ROOM_AREAS_RENDERED = 'virtual-room-areas-rendered',
  VIRTUAL_ROOM_AREAS_HIDDEN = 'virtual-room-areas-hidden',
  VIRTUAL_ROOM_AREAS_ERROR = 'virtual-room-areas-error',
  VIRTUAL_ROOM_AREAS_STATE_CHANGED = 'virtual-room-areas-state-changed',
  PLAYER_ENTERED_VIRTUAL_ROOM = 'player-entered-virtual-room',
  PLAYER_LEFT_VIRTUAL_ROOM = 'player-left-virtual-room',
  PLAYER_CHANGED_VIRTUAL_ROOM = 'player-changed-virtual-room',
  MY_PLAYER_ENTERED_VIRTUAL_ROOM = 'my-player-entered-virtual-room',
  MY_PLAYER_LEFT_VIRTUAL_ROOM = 'my-player-left-virtual-room',
  MY_PLAYER_CHANGED_VIRTUAL_ROOM = 'my-player-changed-virtual-room',
  /** Server VIRTUAL_ROOM_OCCUPANTS roster or authoritative VR membership changed — refresh LiveKit proximity. */
  VIRTUAL_ROOM_OCCUPANTS_UPDATED = 'virtual-room-occupants-updated',
  /** Server VIRTUAL_ROOM_FILTER_STATE changed (ownership, password, authorization). */
  VIRTUAL_ROOM_FILTER_STATE_UPDATED = 'virtual-room-filter-state-updated',
  /** Colyseus transport not open — outgoing messages are being silently dropped. */
  COLYSEUS_TRANSPORT_DEGRADED = 'colyseus-transport-degraded',
  /** Emitted by KeyboardHandler, which swallows Escape at window capture — DOM keydown listeners never see it. */
  ESCAPE_PRESSED = 'escape-pressed',
  // Follow / Escort
  PLAYER_AVATAR_CLICKED = 'player-avatar-clicked',
  FOLLOW_PLAYER_REQUESTED = 'follow-player-requested',
  FOLLOW_PLAYER_CONFIRMED = 'follow-player-confirmed',
  STOP_FOLLOWING_REQUESTED = 'stop-following-requested',
  FOLLOW_STATE_CHANGED = 'follow-state-changed',
  BEING_FOLLOWED_STATE_CHANGED = 'being-followed-state-changed',
  // Teleport (room and player targets — see SC-393/394)
  TELEPORTED = 'teleported',
  TELEPORT_PLAYER_REQUESTED = 'teleport-player-requested',
  /** Someone else teleported nearby — lets an onlooker's OtherPlayer play an arrival fade. */
  PLAYER_TELEPORTED_REMOTE = 'player-teleported-remote',
  /** On-demand full roster response for the teleport picker's People tab (SC-397 phase 2). */
  TELEPORT_PEOPLE_LIST = 'teleport-people-list',
  ESCORT_ARRIVED = 'escort-arrived',
  GUEST_TELEPORTED = 'guest-teleported',
  PLAYER_TELEPORT = 'player-teleport',
  /** Server handed follow movement back after the tab was hidden; payload is the position to adopt. */
  FOLLOW_RESYNC = 'follow-resync',
  /** The Game scene has built its tilemap, layers and object groups, so the map editor can attach. */
  MAP_READY = 'map-ready',
}
